import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MONTHS, ROUTE_DOCS_STEWARDSHIP } from '@/common/constants';
import { IEmployeeActions } from '@/features/stewardship/models/IStewardship';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

type Props = {
  data: IEmployeeActions;
};

export const TableAgrotechEventList: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang, region_id, district_id, employee_id } = useParams();

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleRowClick = () => {
    navigate({
      pathname: `/${lang}/${ROUTE_DOCS_STEWARDSHIP}/${region_id}/${district_id}/${employee_id}/projects`,
      search: window.location.search,
    });
  };

  // Group actions by month
  const groupedData = useMemo(() => {
    const months = new Set<string>();
    data.actions_data.forEach((action) => {
      months.add(action.month);
    });

    const sortedMonths = Array.from(months).sort((a, b) => {
      const monthOrder = MONTHS.map((m) => m.value);
      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
    });

    return sortedMonths.map((month) => ({
      month,
      actions: data.actions_data.filter((action) => action.month === month),
    }));
  }, [data.actions_data]);

  return (
    <div className="w-full h-full rounded-10 border bg-white overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-white">
          <TableRow className="bg-gray-100 hover:bg-gray-100">
            <TableHead className="py-3 !max-w-[50px] w-[50px]">
              <p>#</p>
            </TableHead>
            <TableHead className="py-3">
              <p>{t('crop.name')}</p>
            </TableHead>
            <TableHead className="py-3 text-center">
              <p>{t('farmers.count')}</p>
            </TableHead>
            <TableHead className="py-3 text-center">
              <p>{t('checked.short')}</p>
            </TableHead>
            <TableHead className="py-3 text-center">
              <p>{t('unchecked')}</p>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedData.map((monthGroup) => (
            <React.Fragment key={monthGroup.month}>
              <TableRow className="bg-gray-50 hover:bg-gray-50">
                <TableCell colSpan={5} className="py-3 font-semibold">
                  {t(`${MONTHS.find((m) => m.value === monthGroup.month)?.value}`)}
                </TableCell>
              </TableRow>
              {monthGroup.actions.map((action, index) => (
                <TableRow 
                  key={`${monthGroup.month}-${action.action_name}-${index}`} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={handleRowClick}
                >
                  <TableCell className="py-3 !max-w-[50px] w-[50px]">
                    <p>{index + 1}</p>
                  </TableCell>
                  <TableCell className="py-3">
                    <p className="font-medium">{action.action_name}</p>
                  </TableCell>
                  <TableCell className="py-3 text-center">
                    <p>{formatNumber(action.actions_count)}</p>
                  </TableCell>
                  <TableCell className="py-3 text-center">
                    <p>{formatNumber(action.completed_actions_count)}</p>
                  </TableCell>
                  <TableCell className="py-3 text-center">
                    <p>{formatNumber(action.pending_actions_count)}</p>
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

